import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { api, trackEvent } from './App';
import { ChevronUp, ChevronDown, Close, Warning, ArrowRight, Check } from './Icons';
import { formatDate } from './dateUtils';
import { InfoTooltip } from './InfoTooltip';

/* ── Tiny SVG sparkline ─────────────────────────────────────────────── */
function Sparkline({ data, color, width = 50, height = 20 }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.confidence).filter(v => v !== null);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 0.01;
  const points = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ── Diagnostic card for expanded topic row ──────────────────────── */
function DiagCard({ label, children }) {
  return (
    <div className="diag-card">
      <div className="diag-label">{label}</div>
      {children}
    </div>
  );
}

/* ── Diagnostic zone: shows everything the app knows about a topic ── */
function DiagnosticZone({ diagnostic, topic, onNavigateToSRA }) {
  if (!diagnostic) return <div className="diag-loading">Loading</div>;

  const hist = diagnostic.confidence_history || [];
  const mcq = diagnostic.mcq_stats;
  const sra = diagnostic.sra_stats;
  const recall = diagnostic.recall_stats;
  const lastUpdated = diagnostic.last_updated;
  const hasAnyData = hist.length > 0 || mcq || sra || recall;

  if (!hasAnyData) {
    return (
      <div className="diag-empty">
        No data for this topic. Set your confidence below{onNavigateToSRA && <> or <button className="diag-link" onClick={() => onNavigateToSRA(topic.topic)}>try the SRA questions</button></>}.
      </div>
    );
  }

  // Sparkline data — reverse so oldest is first
  const sparkData = [...hist].reverse();
  const latestConf = topic.confidence;
  const confPct = latestConf !== null ? Math.round(latestConf * 100) : null;
  const cColor = confColor(latestConf);

  return (
    <div className="diag-zone">
      {/* Card 1: Confidence trend */}
      {confPct !== null && (
        <DiagCard label={<>Confidence<InfoTooltip text="How confident you feel about this topic. Adjust the slider to update." /></>}>
          <div className="diag-row">
            <span className="diag-value" style={{ color: cColor }}>{confPct}%</span>
            <Sparkline data={sparkData} color={cColor} />
          </div>
          {hist.length > 1 && (
            <div className="diag-sub">{hist.length} updates</div>
          )}
        </DiagCard>
      )}

      {/* Card 2: MCQ accuracy */}
      {mcq && (
        <DiagCard label={<>MCQ accuracy<InfoTooltip text="Your accuracy on MCQ questions mapped to this topic area." /></>}>
          <span className="diag-value" style={{ color: mcq.accuracy >= 70 ? 'var(--green)' : mcq.accuracy >= 50 ? 'var(--amber)' : 'var(--red)' }}>
            {mcq.accuracy}%
          </span>
          <div className="diag-sub">from {mcq.attempted} question{mcq.attempted !== 1 ? 's' : ''}</div>
        </DiagCard>
      )}

      {/* Card 3: SRA accuracy */}
      {sra && (
        <DiagCard label={<>SRA questions<InfoTooltip text="220 official sample questions from the SRA, split between FLK1 and FLK2. Your results map to topics and feed into your priority dashboard." /></>}>
          <span className="diag-value" style={{ color: sra.accuracy >= 70 ? 'var(--green)' : sra.accuracy >= 50 ? 'var(--amber)' : 'var(--red)' }}>
            {sra.correct} of {sra.attempted}
          </span>
          <div className="diag-sub">{sra.accuracy}% correct · {sra.total - sra.attempted} remaining</div>
        </DiagCard>
      )}

      {/* Card 4: Last recall */}
      {recall && (
        <DiagCard label="Last recall">
          <span className="diag-value">{recall.self_rating || '—'}</span>
          <div className="diag-sub">{recall.days_since === 0 ? 'today' : recall.days_since === 1 ? 'yesterday' : `${recall.days_since} days ago`}</div>
        </DiagCard>
      )}

      {/* Card 5: Last updated */}
      {lastUpdated && (
        <DiagCard label="Last updated">
          <span className="diag-value diag-value-sm">{formatDate(lastUpdated, 'full')}</span>
          {hist.length > 0 && hist[0].status && (
            <div className="diag-sub">{hist[0].status}</div>
          )}
        </DiagCard>
      )}
    </div>
  );
}

const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];

const DEFAULT_STATUS_COLORS = ['#F3F4F6', '#DBEAFE', '#FEF3C7', '#D1FAE5', '#FEE2E2'];

function getStatusColor(status, allStatuses) {
  if (!allStatuses || !status) return null;
  const idx = allStatuses.findIndex(s =>
    typeof s === 'string' ? s === status : s.label === status
  );
  if (idx < 0) return null;
  const item = allStatuses[idx];
  if (typeof item === 'object' && item.color) return item.color;
  return DEFAULT_STATUS_COLORS[idx] || '#F3F4F6';
}


const SRA_ACCOUNTS_LINKS = [
  { title: 'SRA Accounts Rules 2019 (live)', url: 'https://www.sra.org.uk/solicitors/standards-regulations/accounts-rules/', desc: 'The full authoritative rules. Always current.' },
  { title: 'SRA — Keeping client money safe', url: 'https://www.sra.org.uk/solicitors/guidance/keeping-client-money-safe/', desc: 'Guidance on client account obligations' },
  { title: 'SRA — Reporting concerns about client money', url: 'https://www.sra.org.uk/consumers/problems/report-solicitor/', desc: 'SRA reporting framework' },
  { title: 'SRA Standards & Regulations hub', url: 'https://www.sra.org.uk/solicitors/standards-regulations/', desc: 'Full standards and regulations index' },
];

function SRALinks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sra-links-section">
      <button className="history-toggle" onClick={()=>setOpen(o=>!o)}>
        {open?<ChevronUp size={10} />:<ChevronDown size={10} />} SRA source materials
      </button>
      {open&&(
        <div className="sra-links-list">
          <div className="sra-links-warning">
            These link to the SRA website. Current version as published. Do not rely on notes or third-party summaries for rules that may have changed.
          </div>
          {SRA_ACCOUNTS_LINKS.map(l=>(
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="sra-link-item">
              <span className="sra-link-title">{l.title}</span>
              <span className="sra-link-desc">{l.desc}</span>
              <span className="sra-link-arrow"><ArrowRight size={12} /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// Module-level thresholds — updated when Topics component loads settings
let _thRed = 0.4, _thGreen = 0.7;

function confColor(c) {
  if (c===null||c===undefined) return 'var(--muted)';
  if (c<_thRed) return 'var(--red)';
  if (c<_thGreen) return 'var(--amber)';
  return 'var(--green)';
}

function Badge({ c, status }) {
  if (c===null&&!status) return <span className="badge badge-none">Not started</span>;
  if (c===null) return <span className="badge badge-amber">In progress</span>;
  if (c<_thRed) return <span className="badge badge-red">Needs work</span>;
  if (c<_thGreen) return <span className="badge badge-amber">Moderate</span>;
  return <span className="badge badge-green">Strong</span>;
}


function HistoryLog({ topicId }) {
  const [history, setHistory] = useState(null);
  useEffect(()=>{ api(`/api/topics/${topicId}/history`).then(setHistory); },[topicId]);
  if (!history) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading</div>;
  if (history.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No history recorded</div>;
  return (
    <div className="history-log">
      {history.map(h=>(
        <div key={h.id} className="history-row">
          <span className="history-date">{(() => {
            try {
              const d = new Date(h.changed_at.replace(' ', 'T'));
              return d.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) + ' ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
            } catch { return h.changed_at; }
          })()}</span>
          <span className="history-status">{h.status||'—'}</span>
          <span className="history-conf" style={{color:confColor(h.confidence)}}>
            {h.confidence!==null?Math.round(h.confidence*100)+'%':'—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function MCQHistory({ topicId }) {
  const [results, setResults] = useState(null);
  useEffect(()=>{
    api('/api/mcq-results').then(all=>{
      setResults(all.filter(r=>r.topic_id===topicId));
    });
  },[topicId]);

  if (!results) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading</div>;
  if (results.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No MCQ results for this topic. Import a session to populate this view.</div>;

  const withPct = results.filter(r => r.score_pct != null);
  const overallPct = withPct.length > 0
    ? Math.round(withPct.reduce((s,r) => s + r.score_pct, 0) / withPct.length)
    : results.some(r => r.attempted > 0)
      ? Math.round(results.reduce((s,r) => s + r.correct, 0) / results.reduce((s,r) => s + r.attempted, 0) * 100)
      : null;
  const overallColor = overallPct == null ? 'var(--muted)' : overallPct>=70?'var(--green)':overallPct>=50?'var(--amber)':'var(--red)';

  return (
    <div className="mcq-history">
      <div className="mcq-history-summary">
        <span style={{fontSize:12,color:'var(--muted)'}}>Overall accuracy: </span>
        <span style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:500,color:overallColor}}>
          {overallPct != null ? overallPct + '%' : '—'}
        </span>
        <span style={{fontSize:11,color:'var(--muted)'}}> across {results.length} session{results.length!==1?'s':''}</span>
      </div>
      <div className="mcq-history-rows">
        {results.map(r=>{
          const pct = r.score_pct != null ? r.score_pct : (r.attempted > 0 ? Math.round(r.correct/r.attempted*100) : null);
          const c = pct == null ? 'var(--muted)' : pct>=70?'var(--green)':pct>=50?'var(--amber)':'var(--red)';
          const dateStr = (() => { try { return new Date(r.session_date).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}); } catch { return r.session_date; } })();
          return (
            <div key={r.id} className="mcq-history-row" style={{gridTemplateColumns:'90px 1fr 44px 1fr'}}>
              <span className="mcq-history-date">{dateStr}</span>
              <div className="mcq-history-bar-wrap">
                <div className="mcq-history-bar" style={{width:(pct||0)+'%',background:c}}/>
              </div>
              <span className="mcq-history-pct" style={{color:c}}>{pct != null ? pct+'%' : '—'}</span>
              {r.sub_subtopic && r.sub_subtopic !== r.topic_name ? (
                <span style={{fontSize:10,color:'var(--muted)',fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {r.source}: "{r.sub_subtopic}"
                </span>
              ) : (
                <span style={{fontSize:10,color:'var(--hint)'}}>{r.source}</span>
              )}
            </div>
          );
        })}
      </div>
      {results.some(r => r.score_pct != null && r.score_pct < 70) && (
        <div className="mcq-history-weak-note">
          <Warning size={11} /> Some sessions below 70%. Consider recall practice on this topic.
        </div>
      )}
    </div>
  );
}

/* ── Ask Alex micro-tutor ────────────────────────────────────────────────── */
const MAX_ALEX_EXCHANGES = 5;

const ALEX_LIMIT_MESSAGE = "I think we're going in circles on this one. Do a few more questions on the topic, then come back to me if you're still stuck. Sometimes seeing it applied to different facts helps more than me repeating the same explanation.";

function stripMarkdown(text) {
  return text
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')   // **bold** and *italic*
    .replace(/^#{1,6}\s+/gm, '')                 // # headers
    .replace(/^[\s-]*-\s+/gm, '')                // - list items
    .trim();
}

function AskAlex({ questionId, wasCorrect }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role:'user'|'alex', text}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const bottomRef = useRef(null);

  const exhausted = exchangeCount >= MAX_ALEX_EXCHANGES;

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading || exhausted) return;
    const nextExchange = exchangeCount + 1;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api('/api/questions/ask-alex', {
        method: 'POST',
        body: JSON.stringify({ question_id: questionId, message: msg, exchange_number: nextExchange, was_correct: wasCorrect }),
      });
      const cleanText = stripMarkdown(res.response || '');
      const isLast = res.exchanges_remaining === 0;
      setMessages(prev => [...prev, { role: 'alex', text: isLast ? ALEX_LIMIT_MESSAGE : cleanText }]);
      setExchangeCount(nextExchange);
    } catch {
      setMessages(prev => [...prev, { role: 'alex', text: 'Something went wrong. Try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const openLabel = wasCorrect
    ? "Got it right? Alex can explain the reasoning if you're curious."
    : 'Ask Alex about this question';

  if (!open) {
    return (
      <button
        className={`alex-open-btn${wasCorrect ? ' alex-open-btn-correct' : ''}`}
        onClick={() => setOpen(true)}
      >
        {openLabel}
      </button>
    );
  }

  return (
    <div className={`alex-panel${wasCorrect ? ' alex-panel-correct' : ''}`}>
      <div className="alex-header">
        <span className="alex-title">Alex</span>
        <button className="alex-close-btn" onClick={() => setOpen(false)}>×</button>
      </div>
      {messages.length === 0 && (
        <div className="alex-intro">
          {wasCorrect
            ? 'Ask about the reasoning if anything was unclear.'
            : 'Ask a question about this problem. Alex can help you understand why.'}
        </div>
      )}
      <div className="alex-thread">
        {messages.map((m, i) => (
          <div key={i} className={`alex-msg alex-msg-${m.role}`}>
            {m.role === 'alex' && <span className="alex-msg-label">Alex</span>}
            <div className="alex-msg-text">{m.text}</div>
          </div>
        ))}
        {loading && (
          <div className="alex-msg alex-msg-alex">
            <span className="alex-msg-label">Alex</span>
            <div className="alex-msg-text">
              <span className="alex-typing">
                <span className="alex-dot" />
                <span className="alex-dot" />
                <span className="alex-dot" />
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {!exhausted && (
        <div className="alex-input-row">
          <input
            className="alex-input"
            type="text"
            placeholder="Ask a question"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            disabled={loading}
          />
          <button className="alex-send-btn" onClick={handleSend} disabled={loading || !input.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Free calibration quiz ───────────────────────────────────────────────── */
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

function QuizPanel({ topicId, oldConfidence, onClose, onAllAnswered }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [newConfidence, setNewConfidence] = useState(undefined); // undefined = not yet fetched
  const prefetchRef = useRef(null);

  useEffect(() => {
    api(`/api/questions/topic/${topicId}/free`).then(data => {
      setQuestions(data.questions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [topicId]);

  const total = questions.length;
  const currentQ = questions[currentIdx];
  const isLast = currentIdx === total - 1;

  const handleSelect = (letter) => { if (!result) setSelected(letter); };

  const handleSubmit = async () => {
    if (!selected || submitting || !currentQ) return;
    setSubmitting(true);
    const res = await api('/api/questions/answer', {
      method: 'POST',
      body: JSON.stringify({ question_id: currentQ.id, selected_answer: selected }),
    });
    const newAnswers = [...answers, { correct: res.correct }];
    setAnswers(newAnswers);
    setResult(res);
    setSubmitting(false);
    // Pre-fetch new confidence when last question is submitted
    if (newAnswers.length === total) {
      prefetchRef.current = onAllAnswered();
    }
  };

  const handleNext = () => {
    setCurrentIdx(i => i + 1);
    setSelected(null);
    setResult(null);
  };

  const handleSeeResults = async () => {
    setShowResults(true);
    if (prefetchRef.current) {
      const conf = await prefetchRef.current;
      setNewConfidence(conf);
    }
  };

  const getBadgeStyle = (conf) => {
    if (conf === null || conf === undefined) return { bg: '#E5E7EB', color: '#6b6b68' };
    if (conf < _thRed) return { bg: '#FEE2E2', color: '#991B1B' };
    if (conf < _thGreen) return { bg: '#FEF3C7', color: '#92400E' };
    return { bg: '#DCFCE7', color: '#166534' };
  };

  if (loading) {
    return (
      <div className="quiz-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-loading">Loading questions</div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="quiz-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-loading">No questions available for this topic.</div>
        <button className="quiz-close-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  if (showResults) {
    const correct = answers.filter(a => a.correct).length;
    const pct = correct / total;
    const scoreColor = pct >= 0.6 ? '#166534' : pct >= 0.4 ? '#92400E' : '#991B1B';
    const oldPct = oldConfidence !== null && oldConfidence !== undefined ? Math.round(oldConfidence * 100) : null;
    const newPct = newConfidence !== undefined && newConfidence !== null ? Math.round(newConfidence * 100) : null;
    const oldStyle = getBadgeStyle(oldConfidence);
    const newStyle = getBadgeStyle(newConfidence);

    return (
      <div className="quiz-panel quiz-results-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-results-score" style={{ color: scoreColor }}>
          You got {correct} of {total} right
        </div>
        <div className="quiz-results-conf">
          {newConfidence === undefined
            ? 'Updating your readiness score'
            : newPct !== null && oldPct !== null
              ? `Your readiness score updated from ${oldPct}% to ${newPct}%.`
              : newPct !== null
                ? `Your readiness score is now ${newPct}%.`
                : null}
        </div>
        <button className="quiz-close-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  // Build options list from dict or array
  const optionsList = currentQ.options
    ? Array.isArray(currentQ.options)
      ? currentQ.options
      : LETTERS.filter(l => currentQ.options[l]).map(l => ({ letter: l, text: currentQ.options[l] }))
    : [];

  return (
    <div className="quiz-panel" onClick={e => e.stopPropagation()}>
      {/* Progress */}
      <div className="quiz-progress-row">
        <span className="quiz-progress-label">Question {currentIdx + 1} of {total}</span>
        <div className="quiz-dots">
          {Array.from({ length: total }, (_, i) => {
            let cls = 'quiz-dot';
            if (i < answers.length) cls += answers[i].correct ? ' answered-right' : ' answered-wrong';
            else if (i === currentIdx) cls += ' current';
            return <span key={i} className={cls} />;
          })}
        </div>
      </div>

      {/* Scenario */}
      {currentQ.scenario && (
        <div className="quiz-scenario">{currentQ.scenario}</div>
      )}

      {/* Stem */}
      <div className="quiz-stem">{currentQ.stem}</div>

      {/* Options */}
      <div className="quiz-options">
        {optionsList.map(opt => {
          const letter = opt.letter;
          const text = opt.text;
          const isCorrect = result && letter === result.correct_answer;
          const isWrong = result && letter === selected && !result.correct;
          const isGreyed = result && !isCorrect && !isWrong;
          const isSelected = !result && letter === selected;

          const optClass = [
            'quiz-option',
            isSelected ? 'quiz-opt-selected' : '',
            isCorrect ? 'quiz-opt-correct' : '',
            isWrong ? 'quiz-opt-wrong' : '',
            isGreyed ? 'quiz-opt-greyed' : '',
          ].filter(Boolean).join(' ');

          const letterClass = [
            'quiz-letter',
            isCorrect ? 'quiz-letter-correct' : '',
            isWrong ? 'quiz-letter-wrong' : '',
          ].filter(Boolean).join(' ');

          return (
            <button key={letter} className={optClass} onClick={() => handleSelect(letter)}>
              <span className={letterClass}>{letter}</span>
              <span className="quiz-opt-text">{text}</span>
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {result && (
        <div className="quiz-explanation">
          <div className="quiz-explanation-label">Explanation</div>
          <div className="quiz-explanation-text">{result.explanation}</div>
          {!result.correct && result.distractor_misconceptions && result.selected_answer &&
            result.distractor_misconceptions[result.selected_answer] && (
            <div className="quiz-distractor">
              <span className="quiz-distractor-label">Why {result.selected_answer} is wrong:</span>{' '}
              {result.distractor_misconceptions[result.selected_answer]}
            </div>
          )}
          {result.authority && <div className="quiz-authority">{result.authority}</div>}
        </div>
      )}

      {/* Ask Alex — appears after any answered question */}
      {result && currentQ && (
        <AskAlex questionId={currentQ.id} wasCorrect={result.correct} />
      )}

      {/* Action button */}
      {!result ? (
        <button className="quiz-check-btn" disabled={!selected || submitting} onClick={handleSubmit}>
          {submitting ? 'Checking' : 'Check answer'}
        </button>
      ) : isLast ? (
        <button className="quiz-next-btn" onClick={handleSeeResults}>See results</button>
      ) : (
        <button className="quiz-next-btn" onClick={handleNext}>Next question →</button>
      )}
    </div>
  );
}

function TopicRow({ topic, onSave, forceOpen, moduleColor, isFocused, statuses, tourHighlight, sraStat, onNavigateToSRA }) {
  const [open, setOpen] = useState(false);
  const [conf, setConf] = useState(topic.confidence!==null?Math.round(topic.confidence*100):50);
  const [hasConf, setHasConf] = useState(topic.confidence!==null);
  const [status, setStatus] = useState(topic.status||'');
  const [notes, setNotes] = useState(topic.notes||'');
  const [notesFocused, setNotesFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [questionStatus, setQuestionStatus] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const diagCache = useRef(null);
  const qStatusCache = useRef(null);
  const clearTimer = useRef(null);
  const saveTimer = useRef(null);
  const rowRef = useRef(null);

  useEffect(()=>{ if(isFocused){ setOpen(true); setTimeout(()=>rowRef.current?.scrollIntoView({behavior:'smooth',block:'start'}),100); } },[isFocused]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  // Lazy-load diagnostic + question status when expanded (both cached)
  useEffect(()=>{
    if (open && !diagCache.current) {
      api(`/api/topics/${topic.id}/diagnostic`).then(d => {
        diagCache.current = d;
        setDiagnostic(d);
      }).catch(() => setDiagnostic({}));
    } else if (open && diagCache.current) {
      setDiagnostic(diagCache.current);
    }
    if (open && !qStatusCache.current) {
      api(`/api/questions/topic/${topic.id}/status`).then(d => {
        qStatusCache.current = d;
        setQuestionStatus(d);
      }).catch(() => {});
    } else if (open && qStatusCache.current) {
      setQuestionStatus(qStatusCache.current);
    }
  }, [open, topic.id]);

  const save = useCallback(async(confVal,hasC,statusVal,notesVal)=>{
    setSaving(true);
    const confNum = hasC?Math.min(100,Math.max(0,parseInt(confVal)))/100:null;
    const wasUnset = !topic.confidence && topic.confidence !== 0;
    await api(`/api/topics/${topic.id}`,{method:'PATCH',body:JSON.stringify({
      confidence:(isNaN(confNum)||confNum===null)?null:confNum,
      status:statusVal,notes:notesVal,
      date_updated:new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}).replace(/\//g,'.'),
      is_estimated: false,
    })});
    if (hasC && wasUnset) trackEvent('first_confidence_set');
    setSaving(false); setSaved(true);
    setTimeout(()=>setSaved(false),1500);
    onSave&&onSave(topic.id,{confidence:(isNaN(confNum)||confNum===null)?null:confNum,status:statusVal,notes:notesVal,is_estimated:false});
  },[topic.id,onSave]);

  const autoSave=(cVal,hasC,sVal,nVal)=>{
    clearTimeout(saveTimer.current);
    saveTimer.current=setTimeout(()=>save(cVal,hasC,sVal,nVal),600);
  };

  const startClearConfidence=()=>{
    setConfirmClear(true);
    clearTimeout(clearTimer.current);
    clearTimer.current=setTimeout(()=>setConfirmClear(false),3000);
  };
  const confirmClearConfidence=async()=>{
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
    await api(`/api/topics/${topic.id}/clear-confidence`,{method:'POST'});
    setHasConf(false); setConf(50);
    onSave&&onSave(topic.id,{confidence:null});
  };
  const cancelClear=()=>{
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
  };

  const handleOpenQuiz = (e) => {
    e.stopPropagation();
    setQuizOpen(true);
  };

  const handleCloseQuiz = () => {
    setQuizOpen(false);
    // Refresh question status
    api(`/api/questions/topic/${topic.id}/status`).then(d => {
      qStatusCache.current = d;
      setQuestionStatus(d);
    }).catch(() => {});
  };

  const handleAllAnswered = async () => {
    const data = await api(`/api/topics/${topic.module}`);
    const updated = Array.isArray(data) ? data.find(t => t.id === topic.id) : null;
    if (updated) onSave(topic.id, { confidence: updated.confidence });
    return updated?.confidence ?? null;
  };

  const focusRing=isFocused?{boxShadow:`0 0 0 2px ${moduleColor}`,borderColor:moduleColor}:{};
  // Use sub_subtopic (new) or detail (legacy) for the detail text
  const detailText = topic.sub_subtopic || topic.detail || '';

  return (
    <div ref={rowRef} className={`topic-row ${open?'open':''} ${topic.is_estimated?'is-estimated':''}`} style={focusRing}>
      <div className="topic-header" onClick={()=>setOpen(o=>!o)}>
        <div className="topic-info">
          <div className="topic-name">
            {topic.display_name || topic.subtopic}
            {topic.is_estimated && <span className="estimated-badge" title="Estimated from subject-area rating">est.<InfoTooltip text="This confidence was estimated from your MCQ score on a broader topic area. Rate it yourself for a more accurate picture." /></span>}
          </div>
          {detailText && !open && (
            <div className="topic-meta topic-detail-preview">{detailText}</div>
          )}
        </div>
        <div className="topic-right">
          {questionStatus && questionStatus.available > 0 && !quizOpen && (
            <button className="free-q-btn" onClick={handleOpenQuiz}>
              {questionStatus.available} question{questionStatus.available !== 1 ? 's' : ''} left
            </button>
          )}
          {questionStatus && questionStatus.available === 0 && questionStatus.completed > 0 && (
            <span className="q-done-label">{questionStatus.score} correct</span>
          )}
          <Badge c={topic.confidence} status={topic.status}/>
          <div className="conf-wrap">
            <div className="conf-bar-bg"><div className="conf-bar-fill" style={{width:(topic.confidence!==null?Math.round(topic.confidence*100):0)+'%',background:confColor(topic.confidence)}}/></div>
            <span className="conf-pct" style={{color:confColor(topic.confidence)}} aria-label={topic.confidence!==null?`Confidence: ${Math.round(topic.confidence*100)} percent, ${topic.confidence < _thRed ? 'needs work' : topic.confidence < _thGreen ? 'moderate' : 'strong'}`:undefined}>{topic.confidence!==null?Math.round(topic.confidence*100)+'%':'—'}</span>
          </div>
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>

      {quizOpen && (
        <QuizPanel
          topicId={topic.id}
          oldConfidence={topic.confidence}
          onClose={handleCloseQuiz}
          onAllAnswered={handleAllAnswered}
        />
      )}

      {open&&(
        <div className="topic-expand" onClick={e=>e.stopPropagation()}>
          {detailText&&<div className="topic-detail">{detailText}</div>}
          {topic.principle_count > 0 && (
            <div className="topic-principle-count">{topic.principle_count} principles</div>
          )}

          {/* ── Diagnostic zone ── */}
          <DiagnosticZone diagnostic={diagnostic} topic={topic} onNavigateToSRA={onNavigateToSRA} />

          {/* ── Input zone ── */}
          <div className="expand-input-zone">
            {/* Status badge (auto-derived) + optional override dropdown */}
            <div className="status-conf-row">
              <div className="field-group field-group-status">
                <label className="field-label">Status</label>
                <select className="field-select" value={status}
                  style={status ? { background: (getStatusColor(status, statuses) || '#F3F4F6') + '80' } : {}}
                  onChange={e=>{setStatus(e.target.value);autoSave(conf,hasConf,e.target.value,notes);}}>
                  <option value="">— auto —</option>
                  {(statuses||DEFAULT_STATUSES).map(s=><option key={typeof s==='string'?s:s.label} value={typeof s==='string'?s:s.label}>{typeof s==='string'?s:s.label}</option>)}
                </select>
              </div>

              {/* Confidence slider */}
              <div className="field-group field-group-conf">
                <label className="field-label" style={{display:'flex',alignItems:'center',gap:8}}>
                  Confidence
                  {hasConf&&<span style={{color:confColor(conf/100),fontFamily:'var(--mono)',fontSize:13}}>{conf}%</span>}
                </label>
                {!hasConf?(
                  <button className="set-conf-btn" onClick={()=>{setHasConf(true);autoSave(50,true,status,notes);}}>Set confidence</button>
                ):(
                  <div className={`slider-wrap${tourHighlight ? ' tour-highlight' : ''}`}>
                    <input type="range" min="0" max="100" step="5" value={conf} className="conf-slider"
                      style={{'--slider-color':confColor(conf/100), background:`linear-gradient(to right, ${confColor(conf/100)} ${conf}%, var(--border) ${conf}%)`}}
                      onChange={e=>{setConf(parseInt(e.target.value));autoSave(e.target.value,true,status,notes);}}/>
                    <div className="slider-labels"><span>0</span><span>50</span><span>100</span></div>
                    <div className="clear-conf-row">
                      {!confirmClear&&<button className="clear-conf-btn" onClick={startClearConfidence}><Close size={10} /> Clear rating</button>}
                      {confirmClear&&<span className="clear-confirm-inline"><span>Clear this rating?</span><button className="clear-confirm-yes" onClick={confirmClearConfidence}>Yes</button><button className="clear-confirm-no" onClick={cancelClear}>No</button></span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Readiness display — decayed confidence, read-only */}
            {topic.decayed_confidence !== null && topic.decayed_confidence !== undefined && (
              <div className="readiness-row">
                <span className="field-label">
                  Readiness
                  <InfoTooltip text="Calculated from your confidence rating, MCQ accuracy, diagnostic scores, and how recently you reviewed this topic." />
                </span>
                <span className="readiness-value" style={{ color: confColor(topic.decayed_confidence) }}>
                  {Math.round(topic.decayed_confidence * 100)}%
                </span>
              </div>
            )}

            {/* Notes — single-line input that expands on focus */}
            <div className="field-group" style={{marginTop:8}}>
              {(notesFocused || notes) ? (
                <textarea className="field-notes" placeholder="e.g. Weak on s.423 IA 1986, revisit. MCQs drilled ×2."
                  value={notes} autoFocus={notesFocused && !notes}
                  onFocus={() => setNotesFocused(true)}
                  onBlur={() => { if (!notes) setNotesFocused(false); }}
                  onChange={e=>{setNotes(e.target.value);autoSave(conf,hasConf,status,e.target.value);}}/>
              ) : (
                <input className="field-notes-collapsed" type="text" readOnly
                  placeholder="Add a note"
                  onFocus={() => setNotesFocused(true)}
                  onClick={() => setNotesFocused(true)} />
              )}
            </div>

            {/* SRA Links for Solicitors Accounts topics */}
            {topic.topic && topic.topic.toLowerCase().includes('solicitor') && (
              <SRALinks />
            )}
          </div>

          <div className="save-status">
            {saving&&<span className="save-saving">Saving</span>}
            {saved&&!saving&&<span className="save-done">Saved</span>}
            {!saving&&!saved&&topic.date_updated&&<span className="save-date">Updated {formatDate(topic.date_updated, 'relative')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// General Topic group — second level of hierarchy (collapsed by default)
function GeneralTopicGroup({ generalTopic, topics, onSave, forceOpen, moduleColor, focusTopicId, statuses, onRateAll, autoOpen, onNavigateToSRA }) {
  const [open, setOpen] = useState(false);
  const [rateAllOpen, setRateAllOpen] = useState(false);
  const [rateAllVal, setRateAllVal] = useState(50);
  const [rateAllConfirming, setRateAllConfirming] = useState(false);
  const done = topics.filter(t => t.confidence !== null).length;
  const hasAny = done > 0;
  const avg = hasAny ? topics.reduce((s, t) => s + (t.confidence || 0), 0) / topics.length : null;
  const hasFocus = topics.some(t => t.id === focusTopicId);

  useEffect(() => { if (hasFocus) setOpen(true); }, [hasFocus]);
  useEffect(() => { if (forceOpen !== null) setOpen(forceOpen); }, [forceOpen]);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);

  const handleRateAllApply = () => setRateAllConfirming(true);
  const handleRateAllConfirm = () => {
    onRateAll(generalTopic, topics, rateAllVal / 100);
    setRateAllOpen(false);
    setRateAllConfirming(false);
  };
  const handleRateAllCancel = () => { setRateAllConfirming(false); };

  return (
    <div className="general-topic-group">
      <div className="general-topic-header" onClick={() => setOpen(o => !o)} role="heading" aria-level="3">
        <span className="chevron">{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
        <span className="general-topic-name">{generalTopic}</span>
        <div className="general-topic-right">
          {avg !== null && (
            <span className="subject-avg" style={{ color: confColor(avg) }}>
              {Math.round(avg * 100)}%
            </span>
          )}
          <span className="subject-count">{done}/{topics.length}</span>
          <button
            className="rate-all-btn"
            onClick={e => { e.stopPropagation(); setRateAllOpen(o => !o); }}
            title="Set all topics to the same confidence"
          >
            Rate all
          </button>
        </div>
      </div>
      {rateAllOpen && (
        <div className="rate-all-modal" onClick={e => e.stopPropagation()}>
          {!rateAllConfirming ? (
            <>
              <span className="rate-all-label">Set all {topics.length} topics to:</span>
              <input type="range" min="0" max="100" step="5" value={rateAllVal}
                className="conf-slider rate-all-slider"
                style={{'--slider-color':confColor(rateAllVal/100), background:`linear-gradient(to right, ${confColor(rateAllVal/100)} ${rateAllVal}%, var(--border) ${rateAllVal}%)`}}
                onChange={e => setRateAllVal(parseInt(e.target.value))} />
              <span className="rate-all-pct" style={{ color: confColor(rateAllVal / 100) }}>{rateAllVal}%</span>
              <button className="rate-all-confirm" onClick={handleRateAllApply}>Apply</button>
              <button className="rate-all-cancel" onClick={() => { setRateAllOpen(false); setRateAllConfirming(false); }}>Cancel</button>
            </>
          ) : (
            <div className="rate-all-confirm-msg">
              <span>Set all {topics.length} topics to {rateAllVal}%? This overwrites existing ratings.</span>
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button className="rate-all-cancel" onClick={handleRateAllCancel}>Cancel</button>
                <button className="rate-all-confirm" onClick={handleRateAllConfirm}>Confirm</button>
              </div>
            </div>
          )}
        </div>
      )}
      {open && (
        <div className="general-topic-topics">
          {topics.map(t => (
            <TopicRow
              key={t.id}
              topic={t}
              onSave={onSave}
              forceOpen={null}
              moduleColor={moduleColor}
              isFocused={t.id === focusTopicId}
              statuses={statuses}
              sraStat={null}
              onNavigateToSRA={onNavigateToSRA}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Main Topic (subject) group — top level, open by default
function SubjectGroup({ subject, topics, onSave, forceOpen, moduleColor, focusTopicId, statuses, highlightFirstTopic, sraStat, sraTopicStats, onRateAll, autoExpandL2, onNavigateToSRA }) {
  const [open, setOpen] = useState(true);
  const done = topics.filter(t=>t.confidence!==null).length;
  const hasAny = done > 0;
  const avg = hasAny ? topics.reduce((s,t)=>s+(t.confidence||0),0)/topics.length : null;
  const hasFocus = topics.some(t=>t.id===focusTopicId);
  useEffect(()=>{ if(hasFocus) setOpen(true); },[hasFocus]);
  useEffect(()=>{ if(highlightFirstTopic) setOpen(true); },[highlightFirstTopic]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  const sraColor = sraStat?.accuracy == null ? null
    : sraStat.accuracy >= 0.7 ? 'var(--green)' : sraStat.accuracy >= 0.5 ? 'var(--amber)' : 'var(--red)';

  // Group topics by general_topic within this subject
  const byGeneral = {};
  const generalOrder = [];
  topics.forEach(t => {
    const key = t.general_topic || 'General';
    if (!byGeneral[key]) { byGeneral[key] = []; generalOrder.push(key); }
    byGeneral[key].push(t);
  });
  // Remove dupes while preserving order
  const uniqueGenerals = [...new Set(generalOrder)];

  return (
    <div className="subject-group">
      <div className="subject-header" onClick={()=>setOpen(o=>!o)} role="heading" aria-level="2">
        <span className="subject-name" style={{color:moduleColor}}>{subject}</span>
        <div className="subject-right">
          <span className="subject-count">{done}/{topics.length}</span>
          {avg!==null&&<span className="subject-avg" style={{color:confColor(avg)}}>avg {Math.round(avg*100)}%</span>}
          {sraStat?.accuracy != null && (
            <span className="subject-sra-badge" style={{color:sraColor}} title={`SRA questions: ${sraStat.attempted} of ${sraStat.total} attempted, ${Math.round(sraStat.accuracy*100)}% correct`}>
              SRA {Math.round(sraStat.accuracy*100)}%
            </span>
          )}
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>
      {open&&(
        <div className="subject-topics">
          {uniqueGenerals.map((genTopic, gi) => (
            <GeneralTopicGroup
              key={genTopic}
              generalTopic={genTopic}
              topics={byGeneral[genTopic]}
              onSave={onSave}
              forceOpen={forceOpen}
              moduleColor={moduleColor}
              focusTopicId={focusTopicId}
              statuses={statuses}
              onRateAll={onRateAll}
              autoOpen={autoExpandL2}
              onNavigateToSRA={onNavigateToSRA}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Topics({ module, focusTopicId, onFocusConsumed, tourHighlight, onNavigateToSRA }) {
  const [topics, setTopics] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [sraTopicStats, setSraTopicStats] = useState({});
  const [thRed, setThRed] = useState(0.4);
  const [thGreen, setThGreen] = useState(0.7);

  useEffect(() => {
    api('/api/settings').then(s => {
      try {
        const custom = JSON.parse(s.custom_statuses || '[]');
        setStatuses(custom.length > 0 ? custom : DEFAULT_STATUSES);
      } catch { setStatuses(DEFAULT_STATUSES); }
      if (s.confidence_threshold_red) { const v = parseFloat(s.confidence_threshold_red); setThRed(v); _thRed = v; }
      if (s.confidence_threshold_green) { const v = parseFloat(s.confidence_threshold_green); setThGreen(v); _thGreen = v; }
    }).catch((err) => { console.error('[Topics] Failed to load settings:', err); });
  }, []);

  useEffect(() => {
    api('/api/sra/topic-stats').then(data => {
      if (data && typeof data === 'object' && !data.msg) setSraTopicStats(data);
    }).catch((err) => { console.error('[Topics] Failed to load SRA topic stats:', err); });
  }, []);

  const moduleColor = module==='FLK1'?'var(--flk1)':'var(--flk2)';
  const moduleAccent = module==='FLK1'?'rgba(59,130,246,0.3)':'rgba(139,92,246,0.3)';

  useEffect(()=>{
    setLoading(true); setTopics([]); setFilter('all'); setSearch('');
    api(`/api/topics/${module}`).then(data=>{setTopics(data);setLoading(false);});
  },[module]);

  useEffect(()=>{
    if(focusTopicId&&!loading){
      const timer=setTimeout(()=>onFocusConsumed&&onFocusConsumed(),1500);
      return()=>clearTimeout(timer);
    }
  },[focusTopicId,loading,onFocusConsumed]);

  const handleSave=useCallback((id,updates)=>{
    setTopics(prev=>prev.map(t=>t.id===id?{...t,...updates}:t));
  },[]);

  const handleRateAll = useCallback(async (generalTopic, childTopics, confidence) => {
    // Optimistic update
    setTopics(prev => prev.map(t =>
      t.general_topic === generalTopic
        ? { ...t, confidence, is_estimated: true }
        : t
    ));
    await api('/api/topics/cascade-by-subject', {
      method: 'POST',
      body: JSON.stringify({ level: 'L2', general_topic: generalTopic, module, confidence }),
    });
  }, [module]);

  // Search filter (L3 level — determines which L3 topics are visible)
  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return topics;
    return topics.filter(t =>
      (t.subtopic || '').toLowerCase().includes(q) ||
      (t.display_name || '').toLowerCase().includes(q) ||
      (t.main_topic || t.topic || '').toLowerCase().includes(q) ||
      (t.general_topic || '').toLowerCase().includes(q) ||
      (t.sub_subtopic || t.detail || '').toLowerCase().includes(q)
    );
  }, [topics, search]);

  // Compute L2 groups from full unfiltered topics (for filter pill counts)
  const allL2Groups = useMemo(() => {
    const groups = {};
    topics.forEach(t => {
      const key = t.general_topic || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups).map(([name, children]) => {
      const hasAny = children.some(t => t.confidence !== null);
      const avg = hasAny
        ? children.reduce((s, t) => s + (t.confidence || 0), 0) / children.length
        : null;
      return { name, children, avg, hasAny };
    });
  }, [topics]);

  const counts = {
    all: topics.length,
    weak: topics.filter(t => t.confidence !== null && t.confidence < thRed).length,
    untouched: topics.filter(t => t.confidence === null).length,
    strong: topics.filter(t => t.confidence !== null && t.confidence >= thGreen).length,
  };

  // Apply filter pill at L2 level — determine which general topics pass
  const passingL2Names = useMemo(() => {
    if (filter === 'all') return null; // null = show all
    const l2Groups = {};
    searchFiltered.forEach(t => {
      const key = t.general_topic || 'General';
      if (!l2Groups[key]) l2Groups[key] = [];
      l2Groups[key].push(t);
    });
    const passing = new Set();
    Object.entries(l2Groups).forEach(([name, children]) => {
      const hasAny = children.some(t => t.confidence !== null);
      const avg = hasAny
        ? children.reduce((s, t) => s + (t.confidence || 0), 0) / children.length
        : null;
      if (filter === 'weak' && avg !== null && avg < thRed) passing.add(name);
      if (filter === 'untouched' && !hasAny) passing.add(name);
      if (filter === 'strong' && avg !== null && avg >= thGreen) passing.add(name);
    });
    return passing;
  }, [searchFiltered, filter]);

  // Final filtered topics: search-matched + L2-filter-passing
  const filtered = useMemo(() => {
    if (passingL2Names === null) return searchFiltered; // 'all' filter
    return searchFiltered.filter(t => {
      const l2 = t.general_topic || 'General';
      return passingL2Names.has(l2);
    });
  }, [searchFiltered, passingL2Names]);

  // Group by main_topic (uses new column if present, falls back to topic field)
  const grouped = {};
  const subjectOrder = [];
  filtered.forEach(t => {
    const key = t.main_topic || t.topic || 'Other';
    if (!grouped[key]) { grouped[key] = []; subjectOrder.push(key); }
    grouped[key].push(t);
  });
  const uniqueSubjects = [...new Set(subjectOrder)];

  const flk1Desc = 'Business Law · Dispute Resolution · Contract Law · Tort · Legal System · Constitutional & Administrative Law · Legal Services · Ethics';
  const flk2Desc = 'Property Law & Practice · Wills & Administration · Solicitors Accounts · Land Law · Trusts Law · Criminal Liability · Criminal Practice · Ethics';

  if(loading) return <div className="loading">Loading {module}</div>;

  return (
    <div className="topics-page">
      <h1 className="page-heading">{module} topics</h1>
      <div className="module-header-bar" style={{borderColor:moduleAccent,background:moduleAccent.replace('0.3','0.06')}}>
        <span className="module-tag" style={{color:moduleColor}}>{module}</span>
        <span className="module-desc" style={{color:'var(--muted)'}}>
          {module==='FLK1' ? flk1Desc : flk2Desc}
        </span>
      </div>

      <div className="topics-toolbar">
        <input className="search-input" placeholder="Search topics, areas, keywords" value={search} onChange={e=>setSearch(e.target.value)}/>
        <button className="collapse-btn" onClick={()=>setGroupsOpen(true)}>Expand all</button>
        <button className="collapse-btn" onClick={()=>setGroupsOpen(false)}>Collapse all</button>
      </div>

      <div className="filter-row">
        {[
          ['all','All','#3B82F6','#ffffff'],
          ['weak','Needs work','#FEE2E2','#991B1B'],
          ['untouched','Not started','#E5E7EB','#6b6b68'],
          ['strong','Strong','#DCFCE7','#166534'],
        ].map(([k,label,activeBg,activeText])=>(
          <button key={k} className={`filter-btn ${filter===k?'active':''}`}
            style={filter===k?{background:activeBg,borderColor:activeBg,color:activeText}:{}}
            onClick={()=>setFilter(k)}>
            {label} <span className="filter-count">{counts[k]}</span>
          </button>
        ))}
      </div>

      {filtered.length===0&&<div className="empty">No topics match this filter</div>}

      {uniqueSubjects.map((subject, i) => (
        <SubjectGroup
          key={subject}
          subject={subject}
          topics={grouped[subject]}
          onSave={handleSave}
          forceOpen={groupsOpen}
          moduleColor={moduleColor}
          focusTopicId={focusTopicId}
          statuses={statuses}
          highlightFirstTopic={tourHighlight && i === 0}
          sraStat={sraTopicStats[subject] || null}
          sraTopicStats={sraTopicStats}
          onRateAll={handleRateAll}
          autoExpandL2={!!search}
          onNavigateToSRA={onNavigateToSRA}
        />
      ))}
    </div>
  );
}
